import { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { miniInput } from "./budsjettUtils";

export default function AssumptionsDrawer({ settings, computed, onSave, onClose }) {
  const { t } = useLang();
  const inv = settings.inventory || {};
  const [phys, setPhys] = useState({
    venueCapacity: inv.physical?.venueCapacity ?? 730,
    operationalReserve: inv.physical?.operationalReserve ?? 10,
    comps: (inv.physical?.comps || []).map((c) => ({ ...c })),
  });
  const [dinner, setDinner] = useState({
    capacity: inv.dinner?.capacity ?? 300,
    includedExecutive: inv.dinner?.includedExecutive ?? 40,
    partnerSpeakerAllocation: inv.dinner?.partnerSpeakerAllocation ?? 50,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const compsTotal = phys.comps.reduce((a, c) => a + (Number(c.count) || 0), 0);
  const sellablePhys = Math.max(0, (Number(phys.venueCapacity) || 0) - (Number(phys.operationalReserve) || 0) - compsTotal);
  const sellableDinner = Math.max(0, (Number(dinner.capacity) || 0) - (Number(dinner.includedExecutive) || 0) - (Number(dinner.partnerSpeakerAllocation) || 0));

  const setComp = (i, field, v) =>
    setPhys((f) => ({ ...f, comps: f.comps.map((c, j) => (j === i ? { ...c, [field]: v } : c)) }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave({
        physical: {
          venueCapacity: Number(phys.venueCapacity) || 0,
          operationalReserve: Number(phys.operationalReserve) || 0,
          comps: phys.comps
            .filter((c) => String(c.label || "").trim())
            .map((c) => ({ label: c.label, count: Number(c.count) || 0 })),
        },
        dinner: {
          capacity: Number(dinner.capacity) || 0,
          includedExecutive: Number(dinner.includedExecutive) || 0,
          partnerSpeakerAllocation: Number(dinner.partnerSpeakerAllocation) || 0,
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="assumptions-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="sak-drawer relative w-full max-w-[480px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <h2 className="text-[15px] font-semibold">{t("Edit assumptions")}</h2>
          <button type="button" onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="assumptions-close-btn">
            <X size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{t("Physical inventory · Peer Gynt")}</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="adm-label">
              {t("Venue capacity")}
              <input type="number" value={phys.venueCapacity} onChange={(e) => setPhys((f) => ({ ...f, venueCapacity: e.target.value }))} className="adm-input" data-testid="inv-venue-input" />
            </label>
            <label className="adm-label">
              {t("Operational reserve")}
              <input type="number" value={phys.operationalReserve} onChange={(e) => setPhys((f) => ({ ...f, operationalReserve: e.target.value }))} className="adm-input" data-testid="inv-reserve-input" />
            </label>
          </div>
          <p className="adm-label mt-3 mb-1.5">{t("Comps (partner / speaker / press / strategic)")}</p>
          <div data-testid="inv-comps">
            {phys.comps.map((c, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <input
                  value={c.label || ""}
                  onChange={(e) => setComp(i, "label", e.target.value)}
                  className="flex-1 text-[12.5px] border border-[#DED9CD] rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-neutral-900"
                  data-testid={`inv-comp-label-${i}`}
                />
                <input
                  type="number"
                  value={c.count ?? ""}
                  onChange={(e) => setComp(i, "count", e.target.value)}
                  className={miniInput}
                  data-testid={`inv-comp-count-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setPhys((f) => ({ ...f, comps: f.comps.filter((_, j) => j !== i) }))}
                  className="text-neutral-300 hover:text-red-500 transition-colors"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setPhys((f) => ({ ...f, comps: [...f.comps, { label: "", count: 0 }] }))}
              className="inline-flex items-center gap-1 text-[11.5px] text-neutral-500 hover:text-neutral-900 transition-colors mt-1"
              data-testid="inv-comp-add-btn"
            >
              <Plus size={12} /> {t("Add comp category")}
            </button>
          </div>
          <p className="mt-3 text-[12px] tabular-nums text-neutral-600" data-testid="inv-phys-preview">
            {phys.venueCapacity || 0} − {phys.operationalReserve || 0} {t("reserve")} − {compsTotal} {t("comps")} = <b>{sellablePhys} {t("sellable")}</b>
          </p>

          <div className="border-t border-[#F1EEE7] mt-6 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{t("Dinner inventory")}</p>
            <div className="grid grid-cols-3 gap-3">
              <label className="adm-label">
                {t("Capacity")}
                <input type="number" value={dinner.capacity} onChange={(e) => setDinner((f) => ({ ...f, capacity: e.target.value }))} className="adm-input" data-testid="inv-dinner-capacity-input" />
              </label>
              <label className="adm-label">
                {t("Incl. in Executive")}
                <input type="number" value={dinner.includedExecutive} onChange={(e) => setDinner((f) => ({ ...f, includedExecutive: e.target.value }))} className="adm-input" data-testid="inv-dinner-exec-input" />
              </label>
              <label className="adm-label">
                {t("Partner/speaker")}
                <input type="number" value={dinner.partnerSpeakerAllocation} onChange={(e) => setDinner((f) => ({ ...f, partnerSpeakerAllocation: e.target.value }))} className="adm-input" data-testid="inv-dinner-alloc-input" />
              </label>
            </div>
            <p className="mt-3 text-[12px] tabular-nums text-neutral-600" data-testid="inv-dinner-preview">
              {dinner.capacity || 0} − {dinner.includedExecutive || 0} − {dinner.partnerSpeakerAllocation || 0} = <b>{sellableDinner} {t("sellable add-ons")}</b>
            </p>
          </div>

          <p className="mt-6 text-[10.5px] text-neutral-400 leading-relaxed">
            {t("Sellable inventory is derived automatically. Ticket products allocate against it — the Overview warns if products are over-allocated.")}
          </p>
        </div>

        <div className="shrink-0 px-6 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="adm-btn-ghost">{t("Cancel")}</button>
          <button type="button" onClick={save} disabled={saving} className="adm-btn" data-testid="assumptions-save-btn">
            {saving ? t("Saving …") : t("Save assumptions")}
          </button>
        </div>
      </aside>
    </div>
  );
}
